import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { Node, Edge } from '@xyflow/react';
import { 
  noteSpaceService, 
  flowNodeService, 
  flowEdgeService,
  convertFlowNodeToReactFlow,
  convertFlowEdgeToReactFlow,
} from '@/services/supabase';
import { supabase } from '@/integrations/supabase/client';
import { NoteType } from '@/types/notes';

interface StickyNoteData extends Record<string, unknown> {
  type: NoteType;
  content: string;
  isEditing: boolean;
  highlighted?: boolean;
  onSave?: (nodeId: string, content: string) => void;
  onDelete?: (nodeId: string) => void;
  onEdit?: (nodeId: string, isEditing: boolean) => void;
  onHighlight?: (nodeId: string) => void;
  onChangeType?: (nodeId: string, newType: NoteType) => void;
}

// Helper function to migrate old note types to new ones
const migrateNoteType = (oldType: string): NoteType => {
  // Handle backwards compatibility for old note types
  if (oldType === 'observation') {
    return 'behaviour';
  }
  // Ensure the type is valid, default to 'note' if not
  const validTypes: NoteType[] = ['note', 'quote', 'emotion', 'behaviour', 'pain', 'insight', 'opportunity'];
  return validTypes.includes(oldType as NoteType) ? (oldType as NoteType) : 'note';
};

export const useSupabaseNoteSpace = (spaceId: string, reactFlowNodes?: Node[]) => {
  const { user } = useAuth();
  const [nodes, setNodes] = useState<Node[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [sessionName, setSessionName] = useState('Untitled Interview');
  const [questions, setQuestions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [userPermission, setUserPermission] = useState<'view' | 'edit' | 'owner'>('view');

  console.log('useSupabaseNoteSpace hook called with spaceId:', spaceId, 'user:', !!user, 'initialized:', initialized);

  // Change note type - preserve current React Flow position
  const handleChangeNoteType = useCallback(async (nodeId: string, newType: NoteType) => {
    console.log('handleChangeNoteType called:', { nodeId, newType });
    
    try {
      // Get current position from React Flow state, fallback to database state
      const reactFlowNode = reactFlowNodes?.find(n => n.id === nodeId);
      const currentNode = nodes.find(n => n.id === nodeId);
      if (!currentNode) {
        console.error('Node not found for type change:', nodeId);
        return;
      }
      
      const currentData = currentNode.data || {};
      const currentPosition = reactFlowNode?.position || currentNode.position;
      const updatedData = { 
        ...currentData,
        type: newType,
      };
      
      console.log('Updating node type in database:', { nodeId, newType, updatedData, position: currentPosition });
      
      // Update in database with current React Flow position
      await flowNodeService.update(nodeId, {
        data: updatedData,
        position_x: currentPosition.x,
        position_y: currentPosition.y
      });
      
      console.log('Database update successful, updating local state');
      
      // Update local state preserving the exact current position
      setNodes(prev => prev.map(node => 
        node.id === nodeId 
          ? { 
              ...node, 
              position: { ...currentPosition }, // Preserve exact current position
              data: {
                ...updatedData,
                onSave: handleSaveNote,
                onDelete: handleDeleteNote,
                onEdit: handleEditNote,
                onHighlight: handleHighlightNote,
                onChangeType: handleChangeNoteType,
              }
            }
          : node
      ));
      
      console.log('Note type changed successfully:', { nodeId, newType });
    } catch (err) {
      console.error('Error changing note type:', err);
      setError('Failed to change note type');
    }
  }, [nodes, reactFlowNodes]);

  // Save note content - preserve current React Flow position
  const handleSaveNote = useCallback(async (nodeId: string, content: string, onNoteSaved?: () => void) => {
    console.log('handleSaveNote called:', { nodeId, content });
    
    try {
      // Get current position from React Flow state, fallback to database state
      const reactFlowNode = reactFlowNodes?.find(n => n.id === nodeId);
      
      // Use functional update to get current nodes state
      let currentNode: Node | undefined;
      let currentPosition: { x: number; y: number } | undefined;
      
      setNodes(prev => {
        currentNode = prev.find(n => n.id === nodeId);
        if (currentNode) {
          currentPosition = reactFlowNode?.position || currentNode.position;
        }
        return prev; // Don't change anything yet, just capture current state
      });
      
      if (!currentNode) {
        console.error('Node not found for saving:', nodeId);
        return;
      }
      
      // Get the current position from React Flow state
      const currentData = currentNode.data || {};
      const position = currentPosition || { x: 0, y: 0 };
      
      console.log('handleSaveNote - Current node data before save:', { 
        nodeId, 
        currentDataType: currentData.type, 
        reactFlowNodeType: reactFlowNode?.data?.type 
      });
      
      // Preserve the current type from React Flow state, don't migrate unless necessary
      const currentType = currentData.type || reactFlowNode?.data?.type || 'note';
      
      const updatedData = { 
        ...currentData,
        content: content.trim(),
        isEditing: false,
        type: migrateNoteType(String(currentType)),
      };
      
      console.log('Saving node with data:', updatedData, 'position:', position);
      
      // Update in database with the exact current React Flow position
      await flowNodeService.update(nodeId, {
        data: updatedData,
        position_x: position.x,
        position_y: position.y
      });
      
      // Update local state preserving the exact current position
      setNodes(prev => prev.map(node => 
        node.id === nodeId 
          ? { 
              ...node, 
              position: { ...position }, // Preserve exact current position
              data: {
                ...updatedData,
                // Ensure callbacks are preserved with proper binding
                onSave: (nodeId: string, content: string) => handleSaveNote(nodeId, content),
                onDelete: (nodeId: string) => handleDeleteNote(nodeId),
                onEdit: (nodeId: string, isEditing: boolean) => handleEditNote(nodeId, isEditing),
                onHighlight: (nodeId: string) => handleHighlightNote(nodeId),
                onChangeType: (nodeId: string, newType: NoteType) => handleChangeNoteType(nodeId, newType),
              }
            }
          : node
      ));
      
      console.log('Note saved successfully:', { nodeId, type: updatedData.type, position });
      
      // Trigger guidance update if callback provided
      if (onNoteSaved) {
        onNoteSaved();
      }
    } catch (err) {
      console.error('Error saving note:', err);
      setError('Failed to save note');
    }
  }, [reactFlowNodes, handleChangeNoteType]);


  // Delete note
  const handleDeleteNote = useCallback(async (nodeId: string) => {
    console.log('handleDeleteNote called:', nodeId);
    try {
      await flowNodeService.delete(nodeId);
      setNodes(prev => prev.filter(node => node.id !== nodeId));
      setEdges(prev => prev.filter(edge => edge.source !== nodeId && edge.target !== nodeId));
    } catch (err) {
      console.error('Error deleting note:', err);
      setError('Failed to delete note');
    }
  }, []);

  // Edit note - preserve current React Flow position
  const handleEditNote = useCallback(async (nodeId: string, isEditing: boolean) => {
    console.log('handleEditNote called:', { nodeId, isEditing });
    
    try {
      // Get current position from React Flow state, fallback to database state
      const reactFlowNode = reactFlowNodes?.find(n => n.id === nodeId);
      const currentNode = nodes.find(n => n.id === nodeId);
      if (!currentNode) {
        console.error('Node not found for editing:', nodeId);
        return;
      }
      
      const currentData = currentNode.data || {};
      const currentPosition = reactFlowNode?.position || currentNode.position;
      const updatedData = { 
        ...currentData,
        isEditing,
        type: migrateNoteType(String(currentData.type || 'note')),
      };
      
      // Update in database with current React Flow position
      await flowNodeService.update(nodeId, {
        data: updatedData,
        position_x: currentPosition.x,
        position_y: currentPosition.y
      });
      
      // Update local state preserving the exact current position
      setNodes(prev => prev.map(node => 
        node.id === nodeId 
          ? { 
              ...node, 
              position: { ...currentPosition }, // Preserve exact current position
              data: {
                ...updatedData,
                // Ensure callbacks are preserved with proper binding
                onSave: (nodeId: string, content: string) => handleSaveNote(nodeId, content),
                onDelete: (nodeId: string) => handleDeleteNote(nodeId),
                onEdit: (nodeId: string, isEditing: boolean) => handleEditNote(nodeId, isEditing),
                onHighlight: (nodeId: string) => handleHighlightNote(nodeId),
                onChangeType: (nodeId: string, newType: NoteType) => handleChangeNoteType(nodeId, newType),
              }
            }
          : node
      ));
    } catch (err) {
      console.error('Error editing note:', err);
      setError('Failed to update note');
    }
  }, [nodes, reactFlowNodes, handleSaveNote, handleChangeNoteType, handleDeleteNote]);

  // Highlight note - preserve current React Flow position
  const handleHighlightNote = useCallback(async (nodeId: string) => {
    console.log('=== HIGHLIGHT START ===', nodeId);
    const currentNode = nodes.find(n => n.id === nodeId);
    console.log('Current node state before highlight:', currentNode?.data?.highlighted);
    
    try {
      // Get current position from React Flow state, fallback to database state
      const reactFlowNode = reactFlowNodes?.find(n => n.id === nodeId);
      const currentNode = nodes.find(n => n.id === nodeId);
      if (!currentNode) {
        console.error('Node not found for highlighting:', nodeId);
        return;
      }
      
      const currentData = currentNode.data || {};
      const currentPosition = reactFlowNode?.position || currentNode.position;
      const newHighlighted = !currentData.highlighted;
      const updatedData = { 
        ...currentData,
        highlighted: newHighlighted,
        type: migrateNoteType(String(currentData.type || 'note')),
      };
      
      // Update in database with current React Flow position
      await flowNodeService.update(nodeId, {
        data: updatedData,
        position_x: currentPosition.x,
        position_y: currentPosition.y
      });
      
      console.log('About to update local state, new highlighted value:', newHighlighted);
      
      // Update local state preserving the exact current position
      setNodes(prev => prev.map(node => 
        node.id === nodeId 
          ? { 
              ...node, 
              position: { ...currentPosition }, // Preserve exact current position
              data: {
                ...node.data,
                highlighted: newHighlighted,
              }
            }
          : node
      ));
      
      console.log('=== HIGHLIGHT END ===', nodeId, 'new state:', newHighlighted);
    } catch (err) {
      console.error('Error highlighting note:', err);
      setError('Failed to highlight note');
    }
  }, [nodes, reactFlowNodes]);

  // Add new note - ensure type is properly set and persisted
  const handleAddNote = useCallback(async (type: NoteType, reactFlowInstance?: any) => {
    if (!spaceId) {
      console.log('Cannot add note - no spaceId');
      return;
    }
    
    try {
      console.log('Adding new note of type:', type);
      
      const noteData = {
        type: type, // Explicitly set the note type
        content: '',
        isEditing: true,
        highlighted: false
      };
      
      // Get viewport center position
      let centerX = 200;
      let centerY = 200;
      
      if (reactFlowInstance) {
        try {
          const viewport = reactFlowInstance.getViewport();
          const reactFlowWrapper = document.querySelector('.react-flow-wrapper');
          
          if (reactFlowWrapper) {
            const bounds = reactFlowWrapper.getBoundingClientRect();
            
            // Calculate center of visible viewport
            centerX = -(viewport.x / viewport.zoom) + ((bounds.width / 2) / viewport.zoom);
            centerY = -(viewport.y / viewport.zoom) + ((bounds.height / 2) / viewport.zoom);
          }
        } catch (error) {
          console.warn('Could not get viewport center, using default position:', error);
          // Fallback to random position
          centerX = Math.random() * 400 + 100;
          centerY = Math.random() * 400 + 100;
        }
      } else {
        // Fallback to random position when no reactFlowInstance
        centerX = Math.random() * 400 + 100;
        centerY = Math.random() * 400 + 100;
      }
      
      const nodePayload = {
        node_type: 'stickyNote',
        position_x: centerX,
        position_y: centerY,
        data: noteData
      };

      console.log('Creating node with payload:', nodePayload);
      
      const newNode = await flowNodeService.create(spaceId, nodePayload);
      console.log('Created node from database:', newNode);
      
      const reactFlowNode = convertFlowNodeToReactFlow(newNode);
      
      // Create fresh callbacks for the new node to avoid stale closures
      const nodeCallbacks = {
        onSave: (nodeId: string, content: string) => {
          console.log('Fresh callback - handleSaveNote called:', { nodeId, content });
          handleSaveNote(nodeId, content);
        },
        onDelete: (nodeId: string) => {
          console.log('Fresh callback - handleDeleteNote called:', { nodeId });
          handleDeleteNote(nodeId);
        },
        onEdit: (nodeId: string, isEditing: boolean) => {
          console.log('Fresh callback - handleEditNote called:', { nodeId, isEditing });
          handleEditNote(nodeId, isEditing);
        },
        onHighlight: (nodeId: string) => {
          console.log('Fresh callback - handleHighlightNote called:', { nodeId });
          handleHighlightNote(nodeId);
        },
        onChangeType: (nodeId: string, newType: NoteType) => {
          console.log('Fresh callback - handleChangeNoteType called:', { nodeId, newType });
          handleChangeNoteType(nodeId, newType);
        }
      };
      
      // Ensure the type and callbacks are properly set in the React Flow node data
      reactFlowNode.data = {
        ...reactFlowNode.data,
        type: type, // Explicitly preserve the type
        ...nodeCallbacks
      };

      console.log('Final React Flow node:', reactFlowNode);
      setNodes(prev => [...prev, reactFlowNode]);
      
    } catch (err) {
      console.error('Error adding note:', err);
      setError('Failed to add note');
    }
  }, [spaceId, handleSaveNote, handleDeleteNote, handleEditNote, handleHighlightNote, handleChangeNoteType]);

  // Update session name
  const updateSessionName = useCallback(async (name: string) => {
    if (!spaceId || userPermission === 'view') return;
    
    try {
      await noteSpaceService.update(spaceId, { name });
      setSessionName(name);
    } catch (err) {
      console.error('Error updating session name:', err);
    }
  }, [spaceId, userPermission]);

  // Save questions to database
  const saveQuestions = useCallback(async (big3Questions: string[], backupQuestions: string[]) => {
    if (!spaceId || userPermission === 'view') return;
    
    try {
      console.log('Saving questions to database:', { big3Questions, backupQuestions });
      
      const questionsData = {
        big3: big3Questions.map((text, index) => ({
          id: `big3-${Date.now()}-${index}`,
          text,
          type: 'open',
          category: 'big3'
        })),
        backup: backupQuestions.map((text, index) => ({
          id: `backup-${Date.now()}-${index}`,
          text,
          type: 'open',
          category: 'backup'
        }))
      };
      
      await (supabase.from('note_spaces').update({ questions: questionsData }).eq('id', spaceId));
      
      // Update local state
      const questionList = [
        ...questionsData.big3.map((q, i) => ({ ...q, category: 'big3', index: i })),
        ...questionsData.backup.map((q, i) => ({ ...q, category: 'backup', index: i }))
      ];
      setQuestions(questionList);
      
      console.log('Questions saved successfully');
    } catch (err) {
      console.error('Error saving questions:', err);
      setError('Failed to save questions');
    }
  }, [spaceId, userPermission]);

  // Legacy addQuestions method - kept for compatibility
  const addQuestions = useCallback(async (questionTexts: string[]) => {
    if (!spaceId || questionTexts.length === 0 || userPermission === 'view') return;
    
    try {
      console.log('Adding questions:', questionTexts);
      const newQuestions = questionTexts.map((text, index) => ({
        id: `question-${Date.now()}-${index}`,
        text,
        type: 'open' as const,
        category: 'research'
      }));
      
      setQuestions(prev => [...prev, ...newQuestions]);
    } catch (err) {
      console.error('Error adding questions:', err);
    }
  }, [spaceId, userPermission]);

  // Load data from Supabase - ensure types are preserved and callbacks are attached
  const loadData = useCallback(async () => {
    if (!user || !spaceId || initialized) {
      console.log('Skipping loadData:', { user: !!user, spaceId, initialized });
      if (!user || !spaceId) {
        setLoading(false);
      }
      return;
    }
    
    try {
      console.log('Loading data from Supabase for space:', spaceId);
      setLoading(true);
      setError(null);

      // Load note space and check ownership
      const noteSpace = await noteSpaceService.getById(spaceId);
      if (noteSpace) {
        console.log('Loaded note space:', noteSpace.name);
        setSessionName(noteSpace.name);
        
        // Load questions from database
        const dbQuestions = (noteSpace as any).questions || { big3: [], backup: [] };
        const questionList = [
          ...dbQuestions.big3.map((q: any, i: number) => ({ ...q, category: 'big3', index: i })),
          ...dbQuestions.backup.map((q: any, i: number) => ({ ...q, category: 'backup', index: i }))
        ];
        setQuestions(questionList);
        
        const isSpaceOwner = noteSpace.user_id === user.id;
        setIsOwner(isSpaceOwner);
        
        if (isSpaceOwner) {
          setUserPermission('owner');
        } else {
          setUserPermission('edit');
        }
      }

      // Load nodes and preserve note types with proper callback attachment
      const flowNodes = await flowNodeService.getByNoteSpaceId(spaceId);
      console.log('Loaded flow nodes from database:', flowNodes.length);
      
      const questionNodes: any[] = [];
      
      const reactFlowNodes = flowNodes
        .filter(node => node.node_type !== 'question') // Filter out question nodes - handled in component now
        .map(node => {
          const reactFlowNode = convertFlowNodeToReactFlow(node);
          
          // Process sticky notes with proper type preservation and callback attachment
          if (node.node_type === 'stickyNote') {
            console.log('Processing sticky note from database:', {
              id: node.id,
              data: node.data,
              type: node.data?.type
            });
            
            // Ensure the node data has all required properties with proper types
            const nodeData = {
              ...node.data,
              type: migrateNoteType(String(node.data?.type || 'note')),
              content: node.data?.content || '',
              isEditing: node.data?.isEditing || false,
              highlighted: node.data?.highlighted || false,
            };
            
            reactFlowNode.data = {
              ...nodeData,
              // Attach callback functions with proper binding
              onSave: (nodeId: string, content: string) => handleSaveNote(nodeId, content),
              onDelete: (nodeId: string) => handleDeleteNote(nodeId),
              onEdit: (nodeId: string, isEditing: boolean) => handleEditNote(nodeId, isEditing),
              onHighlight: (nodeId: string) => handleHighlightNote(nodeId),
              onChangeType: (nodeId: string, newType: NoteType) => handleChangeNoteType(nodeId, newType),
            };
            
            console.log('Processed sticky note with final data:', {
              id: reactFlowNode.id,
              type: reactFlowNode.data.type,
              content: reactFlowNode.data.content
            });
          }
          
          return reactFlowNode;
        });

      console.log('Final processed nodes count:', reactFlowNodes.length);
      setNodes(reactFlowNodes);

      // Load edges
      const flowEdges = await flowEdgeService.getByNoteSpaceId(spaceId);
      console.log('Loaded flow edges:', flowEdges.length);
      const reactFlowEdges = flowEdges.map(convertFlowEdgeToReactFlow);
      setEdges(reactFlowEdges);

      setInitialized(true);
    } catch (err) {
      console.error('Error loading note space data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [user, spaceId, initialized, handleSaveNote, handleDeleteNote, handleEditNote, handleHighlightNote, handleChangeNoteType]);

  // Load data on mount and when dependencies change
  useEffect(() => {
    if (!initialized) {
      console.log('useEffect triggered - loading data');
      loadData();
    }
  }, [loadData, initialized]);

  return {
    nodes,
    edges,
    sessionName,
    questions,
    loading,
    error,
    isOwner,
    userPermission,
    setNodes,
    setEdges,
    handleSaveNote,
    handleDeleteNote,
    handleEditNote,
    handleHighlightNote,
    handleChangeNoteType,
    handleAddNote: userPermission !== 'view' ? handleAddNote : () => {},
    updateSessionName,
    saveQuestions: userPermission !== 'view' ? saveQuestions : async () => {},
    addQuestions: userPermission !== 'view' ? addQuestions : () => {},
    reload: loadData
  };
};
